import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { Refresh as RefreshIcon, TableBar as TableBarIcon } from "@mui/icons-material";
import { Table } from "../types";
import { petpooja, tableStatusColor } from "../posPetpoojaTheme";

interface TablePanelProps {
  tables: Table[];
  selectedTable: Table | null;
  onSelectTable: (table: Table) => void;
  loading?: boolean;
  onRefresh?: () => void;
  salesPointName?: string;
  onSetupTables?: () => void;
}

const statusLabel: Record<string, string> = {
  vacant: "Blank",
  occupied: "Running",
  reserved: "Reserved",
  blocked: "Blocked",
};

const TablePanel: React.FC<TablePanelProps> = ({
  tables,
  selectedTable,
  onSelectTable,
  loading = false,
  onRefresh,
  salesPointName,
  onSetupTables,
}) => {
  const sections = useMemo(() => {
    const set = new Set<string>();
    tables.forEach((t) => set.add(t.section?.trim() || "Main Area"));
    return ["All", ...Array.from(set)];
  }, [tables]);

  const [sectionTab, setSectionTab] = useState(0);

  const filteredTables =
    sectionTab === 0
      ? tables
      : tables.filter(
          (t) => (t.section?.trim() || "Main Area") === sections[sectionTab]
        );

  const counts = useMemo(
    () => ({
      vacant: tables.filter((t) => t.status === "vacant").length,
      occupied: tables.filter((t) => t.status === "occupied").length,
      reserved: tables.filter((t) => t.status === "reserved").length,
    }),
    [tables]
  );

  return (
    <Box
      sx={{
        width: { xs: 220, md: 260 },
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        bgcolor: petpooja.panel,
        borderRight: `1px solid ${petpooja.border}`,
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          px: 1.5,
          py: 1.25,
          borderBottom: `1px solid ${petpooja.border}`,
          bgcolor: petpooja.header,
          color: petpooja.headerText,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.75 }}>
          <TableBarIcon sx={{ fontSize: 20 }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" fontWeight={700} lineHeight={1.2}>
              Live Tables
            </Typography>
            {salesPointName && (
              <Typography
                variant="caption"
                sx={{ opacity: 0.85, fontSize: "0.65rem", display: "block" }}
              >
                {salesPointName}
              </Typography>
            )}
          </Box>
          <Chip
            label={tables.length}
            size="small"
            sx={{
              ml: "auto",
              height: 20,
              fontSize: "0.7rem",
              bgcolor: "rgba(255,255,255,0.15)",
              color: "#fff",
            }}
          />
          {onRefresh && (
            <Button
              size="small"
              onClick={onRefresh}
              disabled={loading}
              sx={{ minWidth: 0, p: 0.5, color: "#fff" }}
            >
              {loading ? (
                <CircularProgress size={16} sx={{ color: "#fff" }} />
              ) : (
                <RefreshIcon sx={{ fontSize: 18 }} />
              )}
            </Button>
          )}
        </Box>
        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
          {[
            ["Blank", counts.vacant, petpooja.tableVacant],
            ["Running", counts.occupied, petpooja.tableOccupied],
            ["Reserved", counts.reserved, petpooja.tableReserved],
          ].map(([label, count, color]) => (
            <Chip
              key={label as string}
              label={`${label} ${count}`}
              size="small"
              sx={{
                height: 22,
                fontSize: "0.65rem",
                fontWeight: 600,
                bgcolor: color as string,
                color: "#fff",
              }}
            />
          ))}
        </Box>
      </Box>

      {sections.length > 2 && (
        <Tabs
          value={sectionTab}
          onChange={(_, v) => setSectionTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 36,
            borderBottom: `1px solid ${petpooja.border}`,
            bgcolor: petpooja.bg,
            "& .MuiTab-root": {
              minHeight: 36,
              py: 0,
              fontSize: "0.75rem",
              fontWeight: 600,
              textTransform: "none",
            },
            "& .Mui-selected": { color: petpooja.accent },
            "& .MuiTabs-indicator": { bgcolor: petpooja.accent, height: 3 },
          }}
        >
          {sections.map((s) => (
            <Tab key={s} label={s} />
          ))}
        </Tabs>
      )}

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          p: 1.25,
          bgcolor: petpooja.bg,
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 0.75,
          }}
        >
          {filteredTables.map((table) => {
            const bg = tableStatusColor(table.status);
            const selected = selectedTable?.id === table.id;
            return (
              <Box
                key={table.id}
                onClick={() => onSelectTable(table)}
                sx={{
                  aspectRatio: "1",
                  borderRadius: 1,
                  bgcolor: bg,
                  color: "#fff",
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  border: selected
                    ? `3px solid ${petpooja.tableSelected}`
                    : "2px solid transparent",
                  boxShadow: selected
                    ? `0 0 0 2px ${petpooja.accentLight}`
                    : "0 2px 6px rgba(0,0,0,0.12)",
                  transition: "transform 0.15s, box-shadow 0.15s",
                  "&:hover": {
                    transform: "scale(1.04)",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.18)",
                  },
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 800, fontSize: "0.85rem", lineHeight: 1.1 }}
                >
                  {table.name}
                </Typography>
                <Typography variant="caption" sx={{ fontSize: "0.6rem", opacity: 0.9 }}>
                  {table.seats}P
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: "0.55rem",
                    mt: 0.25,
                    px: 0.5,
                    py: 0.1,
                    borderRadius: 0.5,
                    bgcolor: "rgba(255,255,255,0.2)",
                    fontWeight: 600,
                  }}
                >
                  {statusLabel[table.status] || table.status}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {filteredTables.length === 0 && (
          <Box sx={{ textAlign: "center", py: 4, px: 1 }}>
            <TableBarIcon sx={{ fontSize: 40, color: petpooja.border, mb: 1 }} />
            <Typography variant="body2" color={petpooja.textMuted} fontWeight={600}>
              {tables.length === 0
                ? "No tables for this salesPoint"
                : "No tables in this area"}
            </Typography>
            <Typography variant="caption" color={petpooja.textMuted} display="block" sx={{ mt: 0.5 }}>
              {tables.length === 0
                ? onSetupTables
                  ? "Set up tables for this salesPoint"
                  : "No tables configured for this salesPoint"
                : "Try another section tab"}
            </Typography>
            {tables.length === 0 && onSetupTables && (
              <Button
                size="small"
                variant="outlined"
                onClick={onSetupTables}
                sx={{ mt: 1.5, borderColor: petpooja.accent, color: petpooja.accent }}
              >
                Table setup
              </Button>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default TablePanel;
